import { DateTime } from "luxon";
import SHA256 from "crypto-js/sha256";

export const getSignersWithFields = (formFields = [], totalSigners = 3) => {
  return Array.from({ length: totalSigners }).reduce((acc, _, index) => {
    const prefix = `sign_${String.fromCharCode(97 + index)}_`;

    const hasFields = formFields.some(
      (field) =>
        typeof field.name === "string" &&
        field.name.toLowerCase().startsWith(prefix)
    );

    if (hasFields) acc.push(index + 1);
    return acc;
  }, []);
};

export const getNotariesWithFields = (formFields) => {
  return ["a", "b"].reduce((acc, letter, index) => {
    const hasSeal = formFields?.some((f) =>
      f.name?.startsWith(`notary_seal_${letter}_`)
    );
    const hasSign = formFields?.some((f) =>
      f.name?.startsWith(`notary_sign_${letter}_`)
    );

    // Only include if BOTH seal & sign are present
    if (hasSeal && hasSign) acc.push(index); // index 0 = a, index 1 = b

    return acc;
  }, []);
};

export const getUsedSigners = (selectedPackage, currentFormID) => {
  const otherForms = selectedPackage?.process_form_options?.filter(
    (form) => form.formID !== currentFormID
  );

  const mergedSigners = new Set();

  otherForms?.forEach((form) => {
    form.signers_needed?.forEach((signer) => {
      if (signer) mergedSigners.add(signer);
    });
  });

  return [...mergedSigners]; // Returns unique signer IDs
};

export const getIPAndLocation = async (e) => {
  let locationData = {
    ip: "Unknown",
    city: "Unknown",
    region: "Unknown",
    country: "Unknown",
    latitude: null,
    longitude: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    browserTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: "Unknown",
    device: navigator.userAgent || "Unknown",
    org: "Unknown",
    vpnSuspected: false,
    timestamp: DateTime.now().toUTC().toISO(),
  };

  const suspiciousOrgs = [
    "clouvider",
    "m247",
    "nord",
    "contabo",
    "ovh",
    "netprotect",
    "digitalocean",
    "unknown",
  ];

  try {
    const res = await fetch(
      `https://ipinfo.io/json?token=${process.env.REACT_APP_IPINFO_KEY}`
    );
    const data = await res.json();

    const [latitude, longitude] = data.loc ? data.loc.split(",") : [null, null];
    const org = data.org?.toLowerCase() || "unknown";

    // Determine if suspicious
    const isVPN =
      suspiciousOrgs.some((badOrg) => org.includes(badOrg)) ||
      data.timezone !== locationData.browserTZ;

    locationData = {
      ...locationData,
      ip: data.ip,
      city: data.city,
      region: data.region,
      country: data.country,
      latitude,
      longitude,
      timezone: data.timezone,
      org: data.org,
      vpnSuspected: isVPN,
    };
  } catch (err) {
    console.error("Failed to fetch IP/location info:", err);
  }

  // Add platform with fallback
  try {
    if (navigator.userAgentData) {
      const brands = navigator.userAgentData.brands
        .map((b) => `${b.brand} ${b.version}`)
        .join(", ");
      locationData.platform = `${navigator.userAgentData.platform} (${brands})`;
    } else if (navigator.platform) {
      locationData.platform = navigator.platform;
    }
  } catch {
    locationData.platform = "Unknown";
  }

  return locationData;
};

export const getCoords = (e, inputBoxName, coordName, pdfSize, field = {}) => {
  let clicks = {};

  if (e.touches) {
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    clicks = {
      xClick: touch.clientX,
      yClick: touch.clientY,
      isClick: false,
      isTouch: true,
    };
  } else {
    clicks = {
      xClick: e.clientX,
      yClick: e.clientY,
      isClick: true,
      isTouch: false,
    };
  }

  const clickData = {
    [coordName]: {
      x: clicks.xClick,
      y: clicks.yClick,
      isClick: clicks.isClick,
      isTouch: clicks.isTouch,
      url: window.location.href,
      pdfScale: pdfSize,
      timestamp: DateTime.now().toUTC().toISO(),
    },
  };

  if (field && Object.keys(field).length > 0) {
    return {
      [inputBoxName]: {
        x: field.x,
        y: field.y,
        h: field.height,
        w: field.width,
        page: field.page,
        boxID: field.name,
        url: window.location.href,
      },
      ...clickData,
    };
  }

  return clickData;
};

export const createSignatureHash = (hashPayload) => {
  // const hashPayload = {
  //   sessionID,
  //   signerID,
  //   signatureID,
  //   autoInsert,
  //   image,
  //   logs,
  //   meta,
  // };

  const hash = SHA256(JSON.stringify(hashPayload)).toString();

  return hash;
};
